import { FC, useEffect, useRef, useState } from "react";

import useSound from "use-sound";
import { t } from "i18next";

import { makeStyles } from "tss-react/mui";
import { useTheme } from "@mui/material/styles";
import { styles } from "./Timer.styles";

import timerDoneSound from "assets/sounds/timerDone.mp3";
import {
  getPersistedTimerStart,
  persistTimerStart,
} from "services/sessionPersistence";

/** Formats remaining milliseconds as mm:ss (countdown duration, not Unix time). */
const formatCountdownMmSs = (remainingMs: number): string => {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

interface TimerProps {
  timerLength: number;
  sessionId: string | null;
  timerId: string;
  setTimerDone: (done: boolean) => void;
  text: string;
}

const Timer: FC<TimerProps> = ({
  timerLength = 120,
  sessionId,
  timerId,
  setTimerDone,
  text,
}) => {
  const [timerValue, setTimerValue] = useState(() => timerLength * 1000);
  const [timerDone] = useSound(timerDoneSound);
  const countStartedAtRef = useRef<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const theme = useTheme();
  const useStyles = makeStyles()(() => styles(theme));
  const { classes } = useStyles();

  useEffect(() => {
    // Resume countdown after WebView reload instead of restarting from full duration
    const existingStartedAt = getPersistedTimerStart(sessionId, timerId);
    let startedAt: number | undefined;

    if (existingStartedAt !== undefined) {
      const elapsed = Date.now() - existingStartedAt;

      if (elapsed >= timerLength * 1000) {
        // Timer already finished — keep startedAt so further refreshes stay at 00:00
        setTimerDone(true);
        setTimerValue(0);
        return;
      }

      startedAt = existingStartedAt;
      setTimerValue(Math.max(0, timerLength * 1000 - elapsed));
    }

    if (!startedAt) {
      startedAt = Date.now();
      persistTimerStart(sessionId, timerId, startedAt);
    }

    countStartedAtRef.current = startedAt;

    const runTick = (): void => {
      const start = countStartedAtRef.current;
      if (start === null) {
        return;
      }
      const timePassed = Math.ceil((Date.now() - start) / 1000);
      const remainingMs = Math.max(0, (timerLength - timePassed) * 1000);
      setTimerValue(remainingMs);

      if (timePassed > timerLength) {
        setTimerDone(true);
        setTimerValue(0);
        // Keep timerStarts so a refresh after 00:00 recomputes elapsed and stays at 00:00
        return;
      }

      timeoutRef.current = setTimeout(runTick, 1000);
    };

    runTick();

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      countStartedAtRef.current = null;
      setTimerValue(0);
      // Keep timerStarts on unmount so WebView reload can resume elapsed time
    };
  }, [timerLength, sessionId, timerId, setTimerDone]);

  useEffect(() => {
    timerValue === 0 && timerDone();
  }, [timerValue, timerDone]);

  return (
    <div className={classes.timerContainer}>
      <div className={classes.timerText2}>{t(text)}</div>
      <div className={classes.timer} data-test-id="timer-countdown">
        {formatCountdownMmSs(timerValue)}
      </div>
    </div>
  );
};

export default Timer;
